import React from "react";
import { Link } from "react-router-dom";

const UseCases = () => {
  const cases = [
    {
      title: "Indie Developers & Freelancers",
      icon: "terminal",
      items: [
        "Deploy forms without backend code.",
        "Test forms instantly in client websites.",
        "Integrate with webhooks effortlessly.",
        "Deliver professional results faster.",
      ],
    },
    {
      title: "Small Business Owners",
      icon: "storefront",
      items: [
        "See every inquiry in a shared dashboard.",
        "Assign messages or add private notes.",
        "Respond quickly and track lead status.",
        "Never let a customer request slip away.",
      ],
    },
    {
      title: "Digital Agencies & Marketing",
      icon: "business_center",
      items: [
        "Manage multiple clients from one place.",
        "Monitor team activity on a single dashboard.",
        "Create branded forms for each client.",
        "Collaborate seamlessly with internal teams.",
      ],
    },
    {
      title: "Education & Event Organizers",
      icon: "school",
      items: [
        "Collect registrations and feedback securely.",
        "Track student submissions efficiently.",
        "Send automated follow-up notifications.",
        "Analyze responses to improve programs.",
      ],
    },
    {
      title: "Startups & SaaS Companies",
      icon: "rocket_launch",
      items: [
        "Manage sign-ups and demo requests.",
        "Prioritize messages for growing teams.",
        "Scale support systems effortlessly.",
        "Keep communication simple and organized.",
      ],
    },
  ];

  return (
    <section
      id="use-cases"
      className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden border-t border-[#f1f5f9]"
    >
      <div className="mx-auto w-[95%] md:w-[90%]">
        {/* SECTION HEADER */}
        <div className="max-w-3xl mb-12 sm:mb-20 animate-fade-in-up">
          <h2 className="text-sm sm:text-base font-bold tracking-[0.2em] text-[#E63946] uppercase mb-3 sm:mb-4">
            Versatile Solutions
          </h2>
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B1C2D] lg:text-5xl mb-4 sm:mb-6 leading-tight">
            ContactHQ isn’t just another form tool — it’s built for{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0B1C2D] to-[#E63946]">
              how you work.
            </span>
          </h3>
          <p className="text-base sm:text-lg text-[#64748B]">
            Whether you’re a solo developer, small business, or agency, our
            platform adapts to your unique communication needs and lead
            management workflow.
          </p>
        </div>

        {/* USE CASES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {cases.map((useCase, index) => (
            <div
              key={index}
              className="group flex flex-col p-6 sm:p-8 rounded-3xl bg-white border border-[#E2E8F0] hover:border-[#E63946]/40 hover:shadow-2xl hover:shadow-slate-200 transition-all duration-500 opacity-0 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon & Title */}
              <div className="flex items-center gap-4 mb-6">
                <div className="size-12 rounded-xl bg-slate-50 text-[#0B1C2D] flex items-center justify-center group-hover:bg-[#E63946] group-hover:text-white transition-all duration-300 shadow-sm border border-[#E2E8F0]">
                  <span className="material-symbols-outlined text-2xl">
                    {useCase.icon}
                  </span>
                </div>
                <h4 className="text-xl font-bold text-[#0B1C2D]">
                  {useCase.title}
                </h4>
              </div>

              {/* Bullet Points */}
              <ul className="space-y-4 flex-grow">
                {useCase.items.map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-sm text-[#64748B] leading-relaxed"
                  >
                    <span className="material-symbols-outlined text-[#E63946] text-lg flex-none">
                      check_circle
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Action Link */}
              <div className="mt-8 pt-6 border-t border-[#f1f5f9]">
                <button className="text-sm font-bold text-[#0B1C2D] group-hover:text-[#E63946] flex items-center gap-2 transition-colors">
                  Explore {useCase.title.split(" ")[0]} Features
                  <span className="material-symbols-outlined text-sm">
                    arrow_right_alt
                  </span>
                </button>
              </div>
            </div>
          ))}

          {/* FINAL CTA TILE */}
          <div className="flex flex-col p-8 rounded-3xl bg-[#0B1C2D] text-white justify-center items-center text-center opacity-0 animate-fade-in-up [animation-delay:600ms]">
            <span className="material-symbols-outlined text-4xl mb-4 text-[#E63946]">
              add_circle
            </span>
            <h4 className="text-xl font-bold mb-4">Don't see your use case?</h4>
            <p className="text-slate-400 text-sm mb-8">
              ContactHQ is flexible enough for any industry. Let's talk about
              your needs.
            </p>
            <Link to={"/contact"} className="w-full py-3 rounded-xl bg-white text-[#0B1C2D] font-bold hover:bg-[#E63946] hover:text-white transition-all duration-300">
              Contact Our Team
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
